import React, { useState } from "react";
import styled from "styled-components";
import { Dropdown, Menu, Button, Tooltip, Tag } from "antd";
import { MoreOutlined, SearchOutlined, InfoCircleOutlined, TagOutlined, CheckCircleOutlined, UserAddOutlined, FileAddOutlined, FileTextOutlined } from "@ant-design/icons";
import logger, { Logger } from '../../utils/logger';

// Redux imports
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  selectSelectedConversation
} from "../../store/slices/conversationsSlice";
import {
  selectLead
} from "../../store/slices/messagesSlice";
import {
  setShowChatDetails,
  selectShowChatDetails,
  setShowAssignLeadDrawer,
  setShowAssignChatDrawer,
  setOpenForm,
  setShowLabelPopup,
  setShowStatusPopup
} from "../../store/slices/uiSlice";

// Import components
import Profile from "./Profile";
import { Info, FilePlus, FileText, CheckCircle, UserPlus, MoreVertical } from "lucide-react";

// Styled components
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #ffffffb2;
  width: 100%;
`;

const ProfileSection = styled.div`
  display: flex;
  align-items: center;
`;

const UserInfo = styled.div`
  margin-left: 12px;
  
  h2 {
    font-size: 14px;
    margin: 0;
    line-height: 1.2;
    font-family: "Gilroy-Bold";
  }
  
  p {
    font-size: 11px;
    color: #00000080;
    margin: 0;
    font-family: "Gilroy-Bold";
  }
`;


const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #f8f9fa;
  border: none;
  color: #3a4b86;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  &:hover {
    background-color: #ebedf0;
    color: #3a4b86;
  }
`;




const HeaderButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0;
  
  &:hover {
    background-color: #f0f2f5;
  }
`;

interface InboxChatHeadReduxProps {
  setSearchInChat?: (search: boolean) => void;
  addTaskHandler?: () => void;
  addNoteHandler?: () => void;
  onLeadDetailsClick?: () => void; // Add this prop


}

const InboxChatHeadRedux: React.FC<InboxChatHeadReduxProps> = ({
  setSearchInChat,
  addTaskHandler,
  addNoteHandler,
  onLeadDetailsClick // Receive the prop

}) => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const selectedConversation = useAppSelector(selectSelectedConversation);
  const showUserDetails = useAppSelector(selectShowChatDetails);
  const lead = useAppSelector(selectLead);
  const [from, setFrom] = useState<string>("");
  
  // Access userPreferences correctly based on the Redux state structure
  const userPreferences = useAppSelector(state => {
    // Get the preferences from Redux state
    const preferences = state.rootReducers?.user?.userPreferences;
    
    // Return empty array if preferences doesn't exist
    if (!preferences) return [];
    
    // Handle different possible structures of userPreferences
    if (Array.isArray(preferences)) {
      // If it's already an array, return it directly
      return preferences;
    } else if (typeof preferences === 'object' && preferences !== null) {
      // If it's an object with a labels property that's an array, return that
      if ('labels' in preferences && Array.isArray((preferences as any).labels)) {
        return (preferences as any).labels;
      }
    }
    
    // Fallback to empty array
    return [];
  }) as Array<{value: string, [key: string]: any}>;



  // Debug logging
  Logger.log("InboxChatHeadRedux rendered", {
    conversationId: selectedConversation?.id,
    showUserDetails
  });

  // Event handlers
  const handleSearchClick = () => {
    if (setSearchInChat) {
      setSearchInChat(true);
    }
  };

  const handleShowDetails = () => {
    dispatch(setShowChatDetails(!showUserDetails));
  };

  const handleAddTask = () => {
    if (addTaskHandler) {
      addTaskHandler();
    } else {
      dispatch(setOpenForm("task"));
    }
  };

  const handleAddNote = () => {
    if (addNoteHandler) {
      addNoteHandler();
    } else {
      dispatch(setOpenForm("note"));
    }
  };

  const handleOpenLabels = () => {
    if (selectedConversation) {
      // Use the preferences already grabbed at component level
      const labelPreferences = userPreferences || [];
      
      // Map and check which ones are already applied to this conversation
      const labelArray = labelPreferences.map((labelObj: any) => ({
        ...labelObj,
        isLabelChecked: (selectedConversation.label || []).includes(labelObj.value) || false
      }));
      
      // Set the from value to know where we're coming from
      setFrom("chat");
      
      // Set the label data first
      dispatch({
        type: 'SET_LEAD_LABEL_DATA', 
        payload: labelArray
      });
      // or if using a specific action creator function:
      // dispatch(setLeadLabelData(labelArray));
      
      // Then open the popup
      dispatch(setShowLabelPopup(true));
    }
  };

  const handleOpenStatus = () => {
    dispatch(setShowStatusPopup(true));
  };

  const handleAssignLead = () => {
    if (!lead) return;
    
    dispatch(setShowAssignLeadDrawer(true));
  };

  const handleAssignChat = () => {
    if (!selectedConversation) return;
    
    dispatch(setShowAssignChatDrawer(true));
  };

  // Dropdown menu
  const menu = (
    <Menu
      style={{
        width: '220px',  // Wider menu
        padding: '8px 0',
        borderRadius: '12px',
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)'
      }}
    >
      <Menu.Item 
        key="view-profile" 
        icon={<Info size={18} />} 
        onClick={() => {
          logger.log('Menu clicked: View/Hide Details');
          handleShowDetails();
        }}
        style={{ padding: '12px 16px', fontSize: '14px' }}  // Larger padding
      >
        {showUserDetails ? "Hide Details" : "View Details"}
      </Menu.Item>
      
      <Menu.Divider style={{ margin: '8px 0' }} />
      
      <Menu.Item 
        key="add-task" 
        icon={<FilePlus size={18} />} 
        onClick={() => {
          logger.log('Menu clicked: Add Task');
          handleAddTask();
        }}
        style={{ padding: '12px 16px', fontSize: '14px' }}
      >
        Add Task
      </Menu.Item>
      
      <Menu.Item 
        key="add-note" 
        icon={<FileText size={18} />} 
        onClick={() => {
          logger.log('Menu clicked: Add Note');
          handleAddNote();
        }}
        style={{ padding: '12px 16px', fontSize: '14px' }}
      >
        Add Note
      </Menu.Item>
      
      <Menu.Divider style={{ margin: '8px 0' }} />
      
    
      
      
      
      
      
   
    </Menu>
  );

  // If no selected conversation, don't render anything
  if (!selectedConversation) {
    return null;
  }

  return (
    <HeaderContainer>
      <ProfileSection>
        <Profile
          style={{ width: "40px", height: "40px" }}
          src={selectedConversation.userIcon}
          icon="fa-lg"
        />
        <UserInfo>
          <h2>{selectedConversation.name || "Unknown"}</h2>
          <p>{selectedConversation.mobile}</p>
        </UserInfo>
      </ProfileSection>
      
      <ActionButtons>
      <Tooltip title="Lead Details">
      <IconButton 
  type="text" 
  onClick={() => {
    logger.log('Clicked: View Lead Details');
    onLeadDetailsClick && onLeadDetailsClick(); // SOLUTION: Call the correct prop function
  }}
>
  <Info size={30} />
</IconButton>
        </Tooltip>
        
        
        
        <Tooltip title="Add Note">
          <IconButton 
            type="text" 
            onClick={() => {
              logger.log('Clicked: Add Note');
              handleAddNote();
            }}
          >
            <FileText size={18} />
          </IconButton>
        </Tooltip>
        </ActionButtons>
    </HeaderContainer>
  );
};

export default InboxChatHeadRedux;